import type { TicketType } from '@pulsify/shared/dto'
import { Pencil, Trash2 } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { ConfirmButton } from './confirm-button'
import { formatCents } from './ticket-format'

function salesEnded(type: TicketType): boolean {
  return type.salesEndAt !== null && new Date(type.salesEndAt) <= new Date()
}

function formatSalesEnd(iso: string): string {
  return new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}

function soldLabel(type: TicketType): string {
  const sold = String(type.soldCount)
  const paid = type.priceCents > 0
  if (type.quantity === null) return paid ? m.ticketsSold({ sold }) : m.ticketsTaken({ sold })
  const total = String(type.quantity)
  return paid ? m.ticketsSoldOf({ sold, total }) : m.ticketsTakenOf({ sold, total })
}

function priceLabel(type: TicketType): string {
  if (type.priceCents > 0) return formatCents(type.priceCents)
  return type.doorPriceCents
    ? m.ticketsFreeWithDoorPrice({ amount: formatCents(type.doorPriceCents) })
    : m.free()
}

export function TicketTypeList({
  types,
  onEdit,
  onDelete,
  confirmDelete = true,
}: {
  types: TicketType[]
  onEdit: (type: TicketType) => void
  onDelete: (type: TicketType) => void
  confirmDelete?: boolean
}) {
  return (
    <ul className="divide-y divide-border">
      {types.map((type) => (
        <li key={type.id} className="flex items-center gap-2 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="truncate font-medium text-sm">{type.name}</p>
              {type.isStudent ? <Badge variant="secondary">{m.ticketsStudentBadge()}</Badge> : null}
              {type.active ? null : <Badge variant="outline">{m.ticketsOffSale()}</Badge>}
              {salesEnded(type) ? <Badge variant="outline">{m.ticketsSalesEnded()}</Badge> : null}
            </div>
            <p className="text-muted-foreground text-sm">{soldLabel(type)}</p>
            {type.salesEndAt && !salesEnded(type) ? (
              <p className="text-muted-foreground text-sm">
                {m.ticketsSalesUntil({ date: formatSalesEnd(type.salesEndAt) })}
              </p>
            ) : null}
          </div>
          <p className="shrink-0 font-medium text-sm tabular-nums">{priceLabel(type)}</p>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={m.ticketsEditType()}
            onClick={() => onEdit(type)}
          >
            <Pencil />
          </Button>
          {confirmDelete ? (
            type.active || type.soldCount === 0 ? (
              <ConfirmButton
                trigger={
                  <Button variant="ghost" size="icon-sm" aria-label={m.ticketsDeleteType()}>
                    <Trash2 />
                  </Button>
                }
                title={m.ticketsDeleteTitle({ name: type.name })}
                body={m.ticketsDeleteBody()}
                confirmLabel={m.delete()}
                onConfirm={() => onDelete(type)}
              />
            ) : (
              <span aria-hidden className="size-8 shrink-0" />
            )
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={m.ticketsDeleteType()}
              onClick={() => onDelete(type)}
            >
              <Trash2 />
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}
